// Browser auto-translation (Safari/Chrome translate, Google Translate extension)
// swaps text nodes in place without React's knowledge. When React later tries to
// unmount affected subtrees it calls removeChild/insertBefore on the expected
// parent, but the translated replacement node lives under a different parent —
// the native DOM method throws DOMException code 8 (NotFoundError) and the
// whole tree crashes into the ErrorBoundary.
//
// Standard community workaround (React issue #11538): wrap removeChild and
// insertBefore so that a NotFoundError falls back to letting the browser
// resolve the operation itself. This intentionally trades strict DOM semantics
// for resilience — by the time the error fires the node is already gone or
// moved, so returning it is safe.
//
// Applied at module-load time. Guarded for SSG (no window during prerender).
// Idempotent via a marker property so repeated imports (HMR) don't re-wrap.

const MARKER = '__mwpDomSafetyPatched' as const;

type PatchableNode = Node & { [MARKER]?: boolean };

export function applyDomSafetyPatch(): void {
  if (typeof window === 'undefined' || typeof Node === 'undefined') return;
  const proto = Node.prototype as PatchableNode;
  if (proto[MARKER]) return;
  proto[MARKER] = true;

  const origRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        // Node has been relocated (e.g. by a translator). Remove it from its
        // actual parent so the caller's intent — "this node is gone" — holds.
        child.parentNode.removeChild(child);
      }
      return child;
    }
    return origRemoveChild.call(this, child) as T;
  } as typeof proto.removeChild;

  const origInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node isn't actually our child — fall back to append.
      return this.appendChild(newNode) as unknown as T;
    }
    return origInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof proto.insertBefore;
}

applyDomSafetyPatch();
